import { type CountryCode, type ProjectCode } from '@constants'
import { useAnalytics, AnalyticsPlatforms } from '@dni-analysis'
import { usePrevious } from '@dni-react-hooks/use-previous'
import { useApiCreatePopupClickNoShow } from '@hooks-api/fortune/useApiCreatePopupClickNoShow'
import { useApiCreatePopupImpression } from '@hooks-api/fortune/useApiCreatePopupImpression'
import { type PopupCandidatesResponse } from '@hooks-api/types/apiResponse'
import { useState, useEffect, useCallback } from 'react'
import { I18nextProvider } from 'react-i18next'
import { usePopupTriggerService } from './hooks/usePopupTriggerService'
import popupI18nInstance from './i18n/i18nConfig'
import { PopupContainer } from './PopupContainer'
import { canShowPopup } from './utils/canShowPopup'
import { getNextPopupIndex } from './utils/getNextPopupIndex'
import { isRouteMatchPopupRules } from './utils/isRouteMatchPopupRules'
import { popupStorage } from './utils/popupStorage'

const INTERVAL_HOURS_LIMIT_DEFAULT = 1
const DAILY_USER_LIMIT_DEFAULT = 1
const DAILY_TRIGGER_LIMIT_DEFAULT = 1

interface PopupProps {
  projectCode?: ProjectCode
  countryCode?: CountryCode
  campaignId?: number
  popups?: PopupCandidatesResponse['data']['popups']
  intervalHours?: number
  dailyUserLimit?: number
}

export const Popup = (props: PopupProps) => {
  const {
    projectCode,
    countryCode,
    campaignId,
    popups = [],
    intervalHours = INTERVAL_HOURS_LIMIT_DEFAULT,
    dailyUserLimit = DAILY_USER_LIMIT_DEFAULT,
  } = props
  const { trigger: submitPopupImpression } = useApiCreatePopupImpression()
  const { trigger: submitPopupClickNoShow } = useApiCreatePopupClickNoShow()
  const [currentPopupIndex, setCurrentPopupIndex] = useState<number | null>(null)
  const previousPopupIndex = usePrevious(currentPopupIndex)
  const { markPopupShown } = usePopupTriggerService()
  popupStorage.init(campaignId)

  /**
   * 判斷該彈窗是否能顯示（考慮路由 & 彈窗限制）
   * @param index
   * @returns
   */
  const canShowPopupAtIndex = useCallback((index: number) => {
    const popup = popups?.[index]
    if (!popup?.id) return false

    // 優先檢查路由限制
    if (!isRouteMatchPopupRules({
      targetPageType: popup.target_page_type,
      includeTriggerPatterns: popup.include_trigger_patterns,
      excludeTriggerPatterns: popup.exclude_trigger_patterns,
    })) {
      return false
    }
    // 次要檢查其他觸發條件
    return canShowPopup({
      popupId: popup.id.toString(),
      intervalHours: intervalHours || INTERVAL_HOURS_LIMIT_DEFAULT,
      popupDailyLimit: popup.daily_trigger_limit || DAILY_TRIGGER_LIMIT_DEFAULT,
      globalDailyLimit: dailyUserLimit || DAILY_USER_LIMIT_DEFAULT,
      mustShow: popup.must_show,
    })
  }, [popups, intervalHours, dailyUserLimit])

  /**
   * 找出接下來可顯示的彈窗 index
   */
  const findNextPopupIndex = useCallback(() => {
    return getNextPopupIndex({
      popups: popups || [],
      canShowPopupAtIndex,
    })
  }, [canShowPopupAtIndex, popups])

  /**
   * 關閉當前彈窗
   */
  const handleClose = useCallback(() => {
    if (currentPopupIndex === null) return
    const popupId = popups?.[currentPopupIndex]?.id?.toString()
    if (popupId) {
      markPopupShown(popupId)
    }
    setCurrentPopupIndex(null)
  }, [currentPopupIndex, markPopupShown, popups])

  /**
   * 初始化時找接下來要顯示的彈窗
   */
  useEffect(() => {
    const nextPopup = findNextPopupIndex() ?? null
    if (nextPopup !== previousPopupIndex) {
      setCurrentPopupIndex(nextPopup)
    }
  }, [findNextPopupIndex, previousPopupIndex])

  /**
   * 送出曝光
   * @param popupId
   */
  const handleSubmitPopupImpression = useCallback((popupId: string) => {
    submitPopupImpression({
      id: popupId,
      campaign_id: campaignId,
      project_code: campaignId ? undefined : projectCode,
      country_code: campaignId ? undefined : countryCode,
    })
  }, [submitPopupImpression, campaignId, projectCode, countryCode])

  /**
   * 送出點擊今日不再顯示
   * @param popupId
   */
  const handleSubmitPopupClickNoShow = useCallback((popupId: string) => {
    submitPopupClickNoShow({
      id: popupId,
      campaign_id: campaignId,
      project_code: campaignId ? undefined : projectCode,
      country_code: campaignId ? undefined : countryCode,
    })
  }, [submitPopupClickNoShow, campaignId, projectCode, countryCode])

  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  /**
   * 送出點擊彈窗事件
   * @param popupId
   */
  const handlePopupClickEvent = useCallback((popupId: string) => {
    if (popupId) {
      sendEvent({
        eventName: 'popup_click',
        eventParams: {
          popup_id: popupId,
          project_code: campaignId ? undefined : projectCode,
          country_code: countryCode,
          dni_campaign_id: campaignId,
        },
      })
    }
  }, [sendEvent, campaignId, projectCode, countryCode])

  return (
    <I18nextProvider i18n={popupI18nInstance}>
      {popups?.map((popup, index) => {
        const popupId = popup.id?.toString() || ''
        const open = currentPopupIndex === index
        return (
          <PopupContainer
            key={popupId}
            popupKey={popupId}
            popupType={popup.type}
            open={open}
            onClose={handleClose}
            onSubmitPopupImpression={() => handleSubmitPopupImpression(popupId)}
            onSubmitPopupClickNoShow={() => handleSubmitPopupClickNoShow(popupId)}
            title={popup.title || ''}
            content={popup.content || ''}
            image={popup.image_url || ''}
            linkUrl={popup.link_url || ''}
            buttonText={popup.button || ''}
            width={popup.width || 0}
            position={popup.position || undefined}
            onPopupClickEvent={() => handlePopupClickEvent(popupId)}
            backgroundHidden={popup.background_hidden ?? false}
          />
        )
      })}
    </I18nextProvider>
  )
}
